import type AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import type { SharedSnippet } from '../../types/aha-editor';
import { HtmlSerializer } from '../../serialization/HtmlSerializer';
import SerializerInlineParagraph from '../../serialization/htmlSerializers/SerializerInlineParagraph';

class SharedSnippetCommon {
  public static getSnippet(
    controller: AhaEditorV2Controller,
    token: string
  ): SharedSnippet | null | undefined {
    // See if we have the snippet content in cache already.
    const snippetStatus =
      controller.state.pluginState.sharedSnippetCache![token];

    if (typeof (snippetStatus as Promise<SharedSnippet>)?.then === 'function') {
      // The snippet is already loading or doesn't exist.
      return null;
    } else if (snippetStatus) {
      // The snippet is already loaded.
      return snippetStatus as SharedSnippet;
    } else if (snippetStatus === undefined) {
      // We don't have the snippet content yet, so request it.
      this.loadSnippet(controller, token);
      return undefined;
    } else if (!snippetStatus) {
      // The snippet is loaded but didn't exist.
      return null;
    } else {
      // This case can't happen.
      return null;
    }
  }

  static async loadSnippet(
    controller: AhaEditorV2Controller,
    token: string
  ): Promise<void> {
    const provider = controller.ahaEditor.getProvider('sharedSnippet');
    if (!provider) return;

    provider.onGetSnippetContent(
      token,
      (snippetToken: string, snippet: SharedSnippet | null) => {
        // Set or update the snippet in the cache. Because this is in the
        // controller state it will trigger a re-render of the shared snippet.

        // TODO: This is a memory leak because the closure will hold a reference to the controller
        // even after the editor has been destroyed.
        controller.state.pluginState.sharedSnippetCache![snippetToken] =
          snippet;
      }
    );
  }

  // Convert the snippet content to HTML.
  static convertSnippetToHtml(snippet: SharedSnippet): string {
    const editor = HtmlSerializer.createGenericEditor();
    editor.children = snippet.editor_content?.children || [];
    const serializer = new HtmlSerializer(editor, {
      enableFontSizeMark: true,
      enableFontFamilyMark: true,
    });

    // Adds our special SerializerInlineParagraph to replace <p> tags with <span> tags.
    if (snippet.mode === 'inline') {
      serializer.serializers.unshift(SerializerInlineParagraph);
    }

    return serializer.serialize();
  }
}

export default SharedSnippetCommon;
